export interface KoncordeRange {
  range: {
    [field: string]: {
      gte?: Number;
      lte?: Number;
      gt?: Number;
      lt?: Number;
    };
  };
}

export interface KoncordeEquals {
  equals: {
    [field: string]: string;
  };
}

export interface KoncordeRegexp {
  regexp: {
    [field: string]: {
      value: string;
      flags: string;
    };
  };
}

export interface KoncordeIn {
  in: {
    [field: string]: string[];
  };
}
export interface KoncordeAndGroup {
  and: KoncordeClause[];
}
export interface StandardKoncordeFilter {
  or: KoncordeAndGroup[];
}

enum conditionType {
  'threshold' = 'threshold',
  'geofencing' = 'geofencing'
}

enum clauseType {
  'range' = 'range'
  // 'some_geofencing_value' = 'some_geofencing_value'
}

export type KoncordeClause = KoncordeRange /* | KoncordeGeofencing */;

export interface KoncordeAndGroup {
  and: KoncordeClause[];
}

export const createEmptyThreshold = () => ({
  attribute: null,
  type: 'threshold',
  operator: null,
  value: null
});

export const createEmptyGeofencing = () => ({
  attribute: 'position',
  type: 'geofencing',
  operator: null,
  value: {
    center: null,
    radius: null
  }
});

export const fieldTypesAllowed = {
  threshold: ['integer', 'float', 'double', 'long'],
  geofencing: ['geo_point']
};

export interface RuleCondition {
  attribute: string;
  type: conditionType;
  operator: string;
  value: any;
}

export const koncordeToConditions = (
  filter: KoncordeAndGroup
): RuleCondition[] => {
  if (!filter.and) {
    throw new Error(
      'Malformed Koncorde filter: `and` operator not found at root.'
    );
  }

  const conditions: RuleCondition[] = filter.and.map(koncordeClauseToCondition);

  return conditions;
};

export const koncordeClauseToCondition = (
  clause: KoncordeClause
): RuleCondition => {
  /* Koncorde clause shape
  {
    "range": {
      "measures.temperature.degree": {
        "gt": 0
      }
    }
  }
  */
  if (!Object.keys(clause)[0]) {
    throw new Error('Malformed Koncorde clause: cannot find clause name.');
  }
  const clauseType: string = Object.keys(clause)[0];
  const clauseBody = clause[clauseType as keyof typeof clause];
  const attributePath = Object.keys(clauseBody)[0];
  const clauseOperation = clauseBody[attributePath];
  const clauseOperator = Object.keys(clauseOperation)[0];
  const clauseValue =
    clauseOperation[clauseOperator as keyof typeof clauseOperation];

  const condition = {
    attribute: attributePath,
    type: koncordeClauseToConditionType(clauseType),
    operator: clauseOperator,
    value: clauseValue
  };

  return condition;
};

const koncordeClauseToConditionType = (clause: string): conditionType => {
  switch (clause) {
    case 'range':
      return conditionType.threshold;

    case 'some_geofencing_clause':
      return conditionType.geofencing;

    default:
      throw new Error(`Unknow clause type "${clause}"`);
  }
};

const conditionTypeToKoncordeClause = (
  condition: conditionType
): clauseType => {
  switch (condition) {
    case conditionType.threshold:
      return clauseType.range;

    default:
      throw new Error(`Unknown condition type "${condition}"`);
  }
};

export const conditionsToKoncorde = (
  conditions: RuleCondition[]
): KoncordeAndGroup => {
  if (!Array.isArray(conditions)) {
    throw new Error('Malformed conditions (must be an Array)');
  }

  return {
    and: conditions.map(conditionToKoncordeClause)
  };
};

export const conditionToKoncordeClause = (
  condition: RuleCondition
): KoncordeClause => {
  const clauseType: clauseType = conditionTypeToKoncordeClause(condition.type);
  const attributePath = condition.attribute;
  const operator = condition.operator;
  return {
    [clauseType]: {
      [attributePath]: {
        [operator]: condition.value
      }
    }
  };
};
/* Koncorde clause shape
{
  "range": {
    "measures.temperature.degree": {
      "gt": 0
    }
  }
}
*/

/* Condition shape
{
  attribute: 'measures.temperature.degree',
  type: 'threshold',
  operator: 'gt',
  value: 42
}
*/
